import React, { useState } from 'react';
import { Check, Crown, FileText, Calendar, Percent, BookOpen, Sparkles, Pause, RefreshCw, ArrowRight, AlertTriangle, X, Clock, TrendingUp, Shield } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
import { useLanguage } from '../../i18n/LanguageContext';
interface SubscriptionsTabProps {
  theme: ZodiacTheme;
  onNavigate: (page: string) => void;
}
export function SubscriptionsTab({
  theme,
  onNavigate
}: SubscriptionsTabProps) {
  const {
    t
  } = useLanguage();
  const {
    showToast
  } = useToast();
  const [hasSubscription, setHasSubscription] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  // Mock subscription data
  const sub = {
    plan: 'Astriqo Plus',
    price: 990,
    nextBilling: '12 Ноя 2024',
    daysLeft: 12,
    startDate: '12 Окт 2024',
    reportsUsed: 2,
    reportsTotal: 3,
    reportsRemaining: 1,
    savingsAmount: 2400,
    consultationsDiscount: 20,
    discountUsedTimes: 3,
    discountSaved: 1500
  };
  const benefits = [{
    icon: <FileText className="w-4 h-4" data-id="element-20591" />,
    label: t('userdash.subscriptions.benefitReports'),
    desc: t('userdash.subscriptions.benefitReportsDesc'),
    bg: 'bg-indigo-50',
    text: 'text-indigo-600'
  }, {
    icon: <Percent className="w-4 h-4" data-id="element-20592" />,
    label: t('userdash.subscriptions.benefitDiscount'),
    desc: t('userdash.subscriptions.benefitDiscountDesc'),
    bg: 'bg-emerald-50',
    text: 'text-emerald-600'
  }, {
    icon: <BookOpen className="w-4 h-4" data-id="element-20593" />,
    label: t('userdash.subscriptions.benefitArticles'),
    desc: t('userdash.subscriptions.benefitArticlesDesc'),
    bg: 'bg-amber-50',
    text: 'text-amber-600'
  }, {
    icon: <Calendar className="w-4 h-4" data-id="element-20594" />,
    label: t('userdash.subscriptions.benefitCalendar'),
    desc: t('userdash.subscriptions.benefitCalendarDesc'),
    bg: 'bg-purple-50',
    text: 'text-purple-600'
  }];
  const plans = [{
    name: t('userdash.subscriptions.planFree'),
    price: 0,
    features: [t('userdash.subscriptions.featureBasicCalendar'), t('userdash.subscriptions.featureGeneralForecasts'), t('userdash.subscriptions.feature1Profile')],
    current: false
  }, {
    name: 'Astriqo Plus',
    price: 990,
    features: [t('userdash.subscriptions.feature3Reports'), t('userdash.subscriptions.feature20Discount'), t('userdash.subscriptions.featureProCalendar'), t('userdash.subscriptions.featureProArticles'), t('userdash.subscriptions.feature5Profiles')],
    current: false,
    popular: true
  }, {
    name: 'Astriqo Pro',
    price: 1990,
    features: [t('userdash.subscriptions.feature10Reports'), t('userdash.subscriptions.feature30Discount'), t('userdash.subscriptions.featurePrioritySupport'), t('userdash.subscriptions.featureAllPro'), t('userdash.subscriptions.featureUnlimitedProfiles')],
    current: false
  }];
  // No subscription state
  if (!hasSubscription) {
    return <div className="space-y-5" data-id="element-20595">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-20596">
          <div data-id="element-20597">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-20598">
              {t('userdash.subscriptions.title')}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-20599">
              {t('userdash.subscriptions.choosePlan')}
            </p>
          </div>
        </div>

        {/* Social proof */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 flex items-center gap-3" data-id="element-20600">
          <TrendingUp className="w-5 h-5 text-indigo-600 shrink-0" data-id="element-20601" />
          <p className="text-xs text-indigo-800" data-id="element-20602">
            <span className="font-bold" data-id="element-20603">
              {t('userdash.subscriptions.socialProofBold')}
            </span>{' '}
            {t('userdash.subscriptions.socialProofText')}
          </p>
        </div>

        {/* Plan comparison */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-20604">
          {plans.map(plan => <div key={plan.name} className={`bg-white border rounded-xl p-4 relative ${plan.popular ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-slate-200'}`} data-id="element-20605">
              {plan.popular && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-[9px] font-bold px-2.5 py-0.5 rounded-full" data-id="element-20606">
                  {t('userdash.subscriptions.popular')}
                </span>}
              <h4 className="font-bold text-slate-900 text-sm mb-1" data-id="element-20607">
                {plan.name}
              </h4>
              <div className="mb-3" data-id="element-20608">
                {plan.price > 0 ? <span className="text-2xl font-bold text-slate-900" data-id="element-20609">
                    {plan.price} ₽
                    <span className="text-xs font-normal text-slate-500" data-id="element-20610">
                      /{t('userdash.subscriptions.month')}
                    </span>
                  </span> : <span className="text-2xl font-bold text-slate-900" data-id="element-20611">0 ₽</span>}
              </div>
              <ul className="space-y-1.5 mb-4" data-id="element-20612">
                {plan.features.map((f, i) => <li key={i} className="flex items-center gap-1.5 text-xs text-slate-600" data-id="element-20613">
                    <Check className="w-3 h-3 text-emerald-500 shrink-0" data-id="element-20614" /> {f}
                  </li>)}
              </ul>
              <Button variant={plan.popular ? 'primary' : 'outline'} size="sm" className="w-full text-xs" onClick={() => {
            setHasSubscription(true);
            showToast(t('userdash.subscriptions.planActivated', {
              name: plan.name
            }));
          }} data-id="element-20615">
                {plan.price === 0 ? t('userdash.subscriptions.currentPlan') : t('userdash.subscriptions.subscribe')}
              </Button>
            </div>)}
        </div>
      </div>;
  }
  return <div className="space-y-5" data-id="element-20616">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-20617">
        <div data-id="element-20618">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-20619">
            {t('userdash.subscriptions.mySubscription')}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5" data-id="element-20620">
            {t('userdash.subscriptions.manageStats')}
          </p>
        </div>
      </div>

      {/* Status Card */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm relative overflow-hidden" data-id="element-20621">
        <div className="absolute top-0 right-0 opacity-5" data-id="element-20622">
          <Crown className="w-28 h-28 -mt-4 -mr-4" data-id="element-20623" />
        </div>
        <div className="relative z-10" data-id="element-20624">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4" data-id="element-20625">
            <div data-id="element-20626">
              <div className="flex items-center gap-2 mb-1" data-id="element-20627">
                <h3 className="text-xl font-bold text-slate-900" data-id="element-20628">{sub.plan}</h3>
                {isPaused ? <Badge variant="warning" size="sm" className="text-[10px]" data-id="element-20629">
                    {t('userdash.subscriptions.paused')}
                  </Badge> : <Badge variant="success" size="sm" className="text-[10px]" data-id="element-20630">
                    {t('userdash.subscriptions.active')}
                  </Badge>}
              </div>
              <p className="text-slate-500 text-xs" data-id="element-20631">
                {t('userdash.subscriptions.monthlySubscription')}
              </p>
            </div>
            <div className="text-right" data-id="element-20632">
              <div className="text-3xl font-bold text-slate-900" data-id="element-20633">
                {sub.price} ₽
                <span className="text-sm font-normal text-slate-400" data-id="element-20634">
                  {' '}
                  /{t('userdash.subscriptions.month')}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-6" data-id="element-20635">
            <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2" data-id="element-20636">
              <Clock className="w-4 h-4 text-slate-400" data-id="element-20637" />
              <div data-id="element-20638">
                <div className="text-[10px] text-slate-500" data-id="element-20639">
                  {t('userdash.subscriptions.nextBilling')}
                </div>
                <div className="text-xs font-bold text-slate-900" data-id="element-20640">
                  {sub.nextBilling}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2" data-id="element-20641">
              <Calendar className="w-4 h-4 text-slate-400" data-id="element-20642" />
              <div data-id="element-20643">
                <div className="text-[10px] text-slate-500" data-id="element-20644">
                  {t('userdash.subscriptions.timeLeft')}
                </div>
                <div className="text-xs font-bold text-slate-900" data-id="element-20645">
                  {sub.daysLeft} {t('userdash.subscriptions.days')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Stats — "Вы уже получили" */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-20646">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2" data-id="element-20647">
          <Sparkles className="w-4 h-4 text-indigo-600" data-id="element-20648" />
          <span className="text-sm font-bold text-slate-900" data-id="element-20649">
            {t('userdash.subscriptions.youAlreadyGot')}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-100" data-id="element-20650">
          <div className="p-4 text-center" data-id="element-20651">
            <div className="text-2xl font-bold text-indigo-600" data-id="element-20652">
              {sub.reportsUsed}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-20653">
              {t('userdash.subscriptions.reportsUsed')}
            </div>
          </div>
          <div className="p-4 text-center" data-id="element-20654">
            <div className="text-2xl font-bold text-emerald-600" data-id="element-20655">
              {sub.reportsRemaining}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-20656">
              {t('userdash.subscriptions.reportsRemaining')}
            </div>
          </div>
          <div className="p-4 text-center" data-id="element-20657">
            <div className="text-2xl font-bold text-amber-600" data-id="element-20658">
              {sub.savingsAmount.toLocaleString('ru-RU')} ₽
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-20659">
              {t('userdash.subscriptions.savedOnReports')}
            </div>
          </div>
          <div className="p-4 text-center" data-id="element-20660">
            <div className="text-2xl font-bold text-purple-600" data-id="element-20661">
              {sub.discountSaved.toLocaleString('ru-RU')} ₽
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-20662">
              {t('userdash.subscriptions.savedOnDiscounts')}
            </div>
          </div>
        </div>
      </div>

      {/* Reports remaining bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-id="element-20663">
        <div className="flex items-center justify-between mb-2" data-id="element-20664">
          <span className="text-xs font-bold text-slate-900" data-id="element-20665">
            {t('userdash.subscriptions.freeReportsThisMonth')}
          </span>
          <span className="text-xs text-slate-500" data-id="element-20666">
            {t('userdash.subscriptions.usedOfTotal', {
            used: sub.reportsUsed.toString(),
            total: sub.reportsTotal.toString()
          })}
          </span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2.5" data-id="element-20667">
          <div className="bg-indigo-500 h-2.5 rounded-full transition-all" style={{
          width: `${sub.reportsUsed / sub.reportsTotal * 100}%`
        }} data-id="element-20668" />
        </div>
        <p className="text-[10px] text-slate-500 mt-1.5" data-id="element-20669">
          {t('userdash.subscriptions.left')}{' '}
          <span className="font-bold text-indigo-600" data-id="element-20670">
            {sub.reportsRemaining}
          </span>{' '}
          {t('userdash.subscriptions.freeReportsUpdate', {
          date: '12 Ноя 2024'
        })}
        </p>
      </div>

      {/* Benefits */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-20671">
        {benefits.map((b, i) => <div key={i} className="bg-white border border-slate-200 rounded-xl p-3 text-center shadow-sm" data-id="element-20672">
            <div className={`w-9 h-9 rounded-lg ${b.bg} flex items-center justify-center mx-auto mb-2 ${b.text}`} data-id="element-20673">
              {b.icon}
            </div>
            <div className="text-xs font-bold text-slate-900" data-id="element-20674">{b.label}</div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-20675">{b.desc}</div>
          </div>)}
      </div>

      {/* Actions */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3" data-id="element-20676">
        <div className="flex items-center justify-between" data-id="element-20677">
          <h4 className="text-sm font-bold text-slate-900" data-id="element-20678">
            {t('userdash.subscriptions.management')}
          </h4>
          <Badge variant="neutral" size="sm" className="text-[9px]" data-id="element-20679">
            {t('userdash.subscriptions.comingSoon')}
          </Badge>
        </div>
        <p className="text-xs text-slate-500" data-id="element-20680">
          {t('userdash.subscriptions.managementDesc')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-20681">
          <Button variant="primary" size="sm" className="text-xs justify-center opacity-50 cursor-not-allowed" disabled data-id="element-20682">
            <ArrowRight className="w-3.5 h-3.5 mr-1" data-id="element-20683" />{' '}
            {t('userdash.subscriptions.changePlan')}
          </Button>
          <Button variant="outline" size="sm" className="text-xs justify-center opacity-50 cursor-not-allowed" disabled data-id="element-20684">
            <RefreshCw className="w-3.5 h-3.5 mr-1" data-id="element-20685" />{' '}
            {t('userdash.subscriptions.renewEarly')}
          </Button>
          <Button variant="outline" size="sm" className="text-xs justify-center opacity-50 cursor-not-allowed" disabled data-id="element-20686">
            <Pause className="w-3.5 h-3.5 mr-1" data-id="element-20687" />{' '}
            {t('userdash.subscriptions.pause')}
          </Button>
          <Button variant="outline" size="sm" className="text-xs justify-center opacity-50 cursor-not-allowed" disabled data-id="element-20688">
            <X className="w-3.5 h-3.5 mr-1" data-id="element-20689" />{' '}
            {t('userdash.subscriptions.cancelSubscription')}
          </Button>
        </div>
      </div>

      {/* Cancel Warning */}
      {showCancelWarning && <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 space-y-3" data-id="element-20690">
          <div className="flex items-start gap-3" data-id="element-20691">
            <AlertTriangle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" data-id="element-20692" />
            <div data-id="element-20693">
              <h4 className="font-bold text-rose-900 text-sm" data-id="element-20694">
                {t('userdash.subscriptions.areYouSure')}
              </h4>
              <p className="text-xs text-rose-700 mt-1 leading-relaxed" data-id="element-20695">
                {t('userdash.subscriptions.cancelWarning')}
              </p>
              <ul className="mt-2 space-y-1" data-id="element-20696">
                <li className="flex items-center gap-1.5 text-xs text-rose-700" data-id="element-20697">
                  <X className="w-3 h-3 shrink-0" data-id="element-20698" /> {sub.reportsRemaining}{' '}
                  {t('userdash.subscriptions.unusedReports')}
                </li>
                <li className="flex items-center gap-1.5 text-xs text-rose-700" data-id="element-20699">
                  <X className="w-3 h-3 shrink-0" data-id="element-20700" />{' '}
                  {t('userdash.subscriptions.discountLost')}
                </li>
                <li className="flex items-center gap-1.5 text-xs text-rose-700" data-id="element-20701">
                  <X className="w-3 h-3 shrink-0" data-id="element-20702" />{' '}
                  {t('userdash.subscriptions.proFeaturesLost')}
                </li>
              </ul>
              <p className="text-[10px] text-rose-600 mt-2" data-id="element-20703">
                {t('userdash.subscriptions.youSavedTotal')}{' '}
                <span className="font-bold" data-id="element-20704">
                  {(sub.savingsAmount + sub.discountSaved).toLocaleString('ru-RU')}{' '}
                  ₽
                </span>
              </p>
            </div>
          </div>
          <div className="flex gap-2 pt-1" data-id="element-20705">
            <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => setShowCancelWarning(false)} data-id="element-20706">
              {t('userdash.subscriptions.keepSubscription')}
            </Button>
            <Button variant="outline" size="sm" className="flex-1 text-xs text-rose-600 border-rose-300 hover:bg-rose-100" onClick={() => {
          setHasSubscription(false);
          setShowCancelWarning(false);
          showToast(t('userdash.subscriptions.cancelledNotice'));
        }} data-id="element-20707">
              {t('userdash.subscriptions.cancelAnyway')}
            </Button>
          </div>
        </div>}

      {/* Savings highlight */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-3" data-id="element-20708">
        <Shield className="w-5 h-5 text-emerald-600 shrink-0" data-id="element-20709" />
        <p className="text-xs text-emerald-800" data-id="element-20710">
          {t('userdash.subscriptions.totalSavedHighlight')}{' '}
          <span className="font-bold" data-id="element-20711">
            {(sub.savingsAmount + sub.discountSaved).toLocaleString('ru-RU')} ₽
          </span>
          . {t('userdash.subscriptions.averageSavings')}
        </p>
      </div>
    </div>;
}